import * as React from 'react';
import { Loader2, Play, Sparkles, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Switch } from '../../../../components/ui/switch';
import { Slider } from '../../../../components/ui/slider';
import { Input } from '../../../../components/ui/input';
import { useToast } from '../../../../components/ui/toast';
import {
  useDeleteFiller,
  useFillers,
  useGenerateFillers,
  useUpdateFiller,
  type FillerEntry,
} from '../../../../hooks/useFillers';

interface Props {
  trigger: React.ReactNode;
  category?: string;
  voiceId: string;
  language: string;
}

export function FillerLibraryDialog({ trigger, category, voiceId, language }: Props) {
  const fillers = useFillers();
  const generate = useGenerateFillers();
  const update = useUpdateFiller();
  const remove = useDeleteFiller();
  const { toast } = useToast();
  const [playingId, setPlayingId] = React.useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const items = React.useMemo(() => {
    if (!fillers.data) return [];
    return category ? fillers.data.filter((f) => f.category === category) : fillers.data;
  }, [fillers.data, category]);

  function play(item: FillerEntry) {
    if (!item.previewUrl) {
      toast({ title: 'Kein Audio vorhanden', description: 'Generiere die Datei zuerst neu.', variant: 'destructive' });
      return;
    }
    audioRef.current?.pause();
    const a = new Audio(item.previewUrl);
    audioRef.current = a;
    setPlayingId(item.id);
    a.onended = () => setPlayingId(null);
    a.onerror = () => {
      setPlayingId(null);
      toast({
        title: 'Wiedergabe fehlgeschlagen',
        description:
          'Der Audio-Stream konnte nicht geladen werden. Falls das nach einem Re-Generate weiter passiert: Storage-CORS-Konfiguration prüfen.',
        variant: 'destructive',
      });
    };
    void a.play().catch(() => setPlayingId(null));
  }

  async function regen(force: boolean) {
    try {
      const r = await generate.mutateAsync({ voiceId, language, force });
      toast({
        title: force ? 'Filler überschrieben' : 'Filler erzeugt',
        description: `${r.written.length} neu, ${r.skipped.length} übersprungen.`,
      });
    } catch (err) {
      toast({ title: 'Generierung fehlgeschlagen', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Filler-Bibliothek</DialogTitle>
          <DialogDescription>
            Vorab-encodierte μ-law 8 kHz Audios, die der Voice-Agent zwischen User-Turn und
            LLM-Antwort einspielt. Auto-Generiert via ElevenLabs in deiner aktuellen Stimme.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => regen(false)} disabled={generate.isPending} size="sm">
            {generate.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Default-Set generieren
          </Button>
          <Button onClick={() => regen(true)} disabled={generate.isPending} size="sm" variant="outline">
            Komplett neu generieren (überschreiben)
          </Button>
          <div className="ml-auto text-xs text-muted-foreground">
            Voice-ID <code className="rounded bg-muted px-1">{voiceId.slice(0, 10)}…</code> · Sprache <code className="rounded bg-muted px-1">{language}</code>
          </div>
        </div>

        {fillers.isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Filler …
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Noch keine Filler. Klicke „Default-Set generieren".
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2"></th>
                  <th className="px-2 py-2">Label</th>
                  <th className="px-2 py-2">Kategorie</th>
                  <th className="px-2 py-2">Sprache</th>
                  <th className="px-2 py-2">Aktiv</th>
                  <th className="px-2 py-2 w-32">Gewicht</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-border/50 last:border-0">
                    <td className="px-2 py-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        disabled={!it.previewUrl}
                        onClick={() => play(it)}
                        aria-label={`${it.label} abspielen`}
                      >
                        {playingId === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                      </Button>
                    </td>
                    <td className="px-2 py-2 font-medium">{it.label}</td>
                    <td className="px-2 py-2 text-muted-foreground">{it.category}</td>
                    <td className="px-2 py-2 text-muted-foreground">{it.language}</td>
                    <td className="px-2 py-2">
                      <Switch
                        checked={it.active}
                        onCheckedChange={(v) => update.mutate({ id: it.id, active: v })}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Slider
                        value={[it.weight]}
                        min={0}
                        max={1}
                        step={0.05}
                        onValueChange={(v) => update.mutate({ id: it.id, weight: v[0] ?? it.weight })}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={async () => {
                          await remove.mutateAsync(it.id);
                          toast({ title: 'Filler gelöscht' });
                        }}
                        aria-label={`${it.label} löschen`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-muted-foreground">
              Hinweis: Filler werden als WAV-PCM 16-bit @ 16 kHz gespeichert
              und sind im Browser direkt abspielbar. Im echten Anruf
              re-encodiert Pipecat sie automatisch nach μ-law 8 kHz für
              Twilio.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Manual upload of a pre-encoded .ulaw file is intentionally NOT shipped
// in B4 — the auto-generate flow already produces idiomatic μ-law output
// from the user's chosen voice. Custom audio uploads + ffmpeg conversion
// are deferred to a follow-up so we don't introduce a heavyweight
// transcode pipeline in this slice. Components are structured so that
// flow can be added by extending FillerLibraryDialog without touching
// the runtime FillerInjector path.
export const _UploadDeferredMarker = true;
